import React, { useEffect, useRef, useState } from 'react';
import { Btn, Msg, Section, Select, TextInput, panelPost } from '../kit';
import { SpinnerIcon } from '../../icons/SpinnerIcon';
import { CosmosLogoIcon } from '../../icons';

/**
 * Admin → Tech URL pages → Sections & access → Page transition.
 *
 * How a public tech/university page moves to the next one inside the site
 * (similar technologies, a university's tech cards): the page being left dims
 * under the site loader for at least `min_loader_ms`, then the new page enters
 * with `effect` over `duration_ms`. Saved in tech_pages settings
 * (`transition`) and sent with every page, so no frontend deploy is needed to
 * change it. "Play" previews the combination here.
 */

type Transition = {
  enabled: boolean;
  effect: 'fade' | 'fade-up' | 'slide' | 'none';
  duration_ms: number;
  min_loader_ms: number;
  loader: 'spinner' | 'logo' | 'bar' | 'none';
};

const DEFAULT: Transition = { enabled: true, effect: 'fade-up', duration_ms: 320, min_loader_ms: 350, loader: 'spinner' };
const EFFECT_LABELS: Record<string, string> = {
  fade: 'Fade in', 'fade-up': 'Fade in, rising', slide: 'Slide in from the right', none: 'No effect (instant)',
};
const LOADER_LABELS: Record<string, string> = {
  spinner: 'Site spinner', logo: 'Cosmos logo + spinner', bar: 'Thin bar along the top', none: 'No loader',
};

const KEYFRAMES = `
@keyframes ptx-fade { from { opacity: 0 } to { opacity: 1 } }
@keyframes ptx-fade-up { from { opacity: 0; transform: translateY(14px) } to { opacity: 1; transform: none } }
@keyframes ptx-slide { from { opacity: 0; transform: translateX(22px) } to { opacity: 1; transform: none } }
@keyframes ptx-bar { 0% { left: -40% } 100% { left: 100% } }
`;

const norm = (t: any): Transition => ({
  enabled: typeof t?.enabled === 'boolean' ? t.enabled : DEFAULT.enabled,
  effect: ['fade', 'fade-up', 'slide', 'none'].includes(t?.effect) ? t.effect : DEFAULT.effect,
  duration_ms: Number.isFinite(Number(t?.duration_ms)) ? Number(t.duration_ms) : DEFAULT.duration_ms,
  min_loader_ms: Number.isFinite(Number(t?.min_loader_ms)) ? Number(t.min_loader_ms) : DEFAULT.min_loader_ms,
  loader: ['spinner', 'logo', 'bar', 'none'].includes(t?.loader) ? t.loader : DEFAULT.loader,
});

const Preview: React.FC<{ t: Transition; run: number }> = ({ t, run }) => {
  const [phase, setPhase] = useState<'idle' | 'loading' | 'enter'>('idle');
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!run) return;
    window.clearTimeout(timer.current);
    if (!t.enabled) { setPhase('enter'); return; }
    setPhase(t.loader === 'none' ? 'enter' : 'loading');
    if (t.loader !== 'none') timer.current = window.setTimeout(() => setPhase('enter'), Math.max(0, t.min_loader_ms));
    return () => window.clearTimeout(timer.current);
  }, [run]); // eslint-disable-line react-hooks/exhaustive-deps
  const anim = t.enabled && t.effect !== 'none' && phase === 'enter'
    ? `ptx-${t.effect} ${Math.max(0, t.duration_ms)}ms cubic-bezier(.2,.7,.2,1) both` : undefined;
  return (
    <div className="relative h-44 rounded-lg border border-grayscale-300 bg-[#F4F2EE] overflow-hidden">
      <style>{KEYFRAMES}</style>
      <div key={`${run}:${phase}`} className="p-4 flex flex-col gap-2"
        style={{ opacity: phase === 'loading' ? 0.35 : 1, animation: anim }}>
        <div className="text-[15px] font-bold text-[#1B1C1E]">Genetically Improved Freshwater <span className="text-[#725737]">Prawn for Aquaculture</span></div>
        <div className="flex gap-2">
          <span className="h-6 w-28 rounded-md bg-[#886A44]" />
          <span className="h-6 w-28 rounded-md border border-grayscale-400 bg-white" />
        </div>
        <span className="h-2.5 w-4/5 rounded bg-[#E3D8C7]" />
        <span className="h-2.5 w-3/5 rounded bg-[#E3D8C7]" />
        <span className="h-2.5 w-2/3 rounded bg-[#E3D8C7]" />
      </div>
      {phase === 'loading' && t.loader === 'bar' && (
        <div className="absolute top-0 left-0 right-0 h-[3px] overflow-hidden">
          <span className="absolute top-0 bottom-0 w-2/5 bg-[#886A44]" style={{ animation: 'ptx-bar .9s ease-in-out infinite' }} />
        </div>
      )}
      {phase === 'loading' && (t.loader === 'spinner' || t.loader === 'logo') && (
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2 rounded-2xl bg-white/95 px-4 py-3 shadow">
          {t.loader === 'logo' && <div className="w-12 h-12 p-2 rounded-xl bg-primary-100 flex items-center justify-center"><CosmosLogoIcon /></div>}
          <SpinnerIcon />
        </div>
      )}
    </div>
  );
};

const PageTransition: React.FC<{ data: any; onSaved?: (data: any) => void }> = ({ data, onSaved }) => {
  const [t, setT] = useState<Transition>(norm(data?.transition));
  const [base, setBase] = useState(JSON.stringify(norm(data?.transition)));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [run, setRun] = useState(0);

  useEffect(() => {
    const n = norm(data?.transition);
    setT(n); setBase(JSON.stringify(n));
  }, [data]);

  const dirty = JSON.stringify(t) !== base;
  const set = <K extends keyof Transition>(k: K, v: Transition[K]) => setT(x => ({ ...x, [k]: v }));
  const ms = (v: string) => Math.max(0, Math.min(3000, Math.round(Number(v) || 0)));

  const save = async () => {
    setBusy(true); setMsg(null);
    const { data: out, error } = await panelPost('tech_pages', 'settings_save', { transition: t });
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    if (out && typeof out === 'object') {
      const n = norm((out as any).transition);
      setT(n); setBase(JSON.stringify(n)); onSaved?.(out);
    }
    setMsg({ kind: 'ok', text: 'Saved. Pages pick it up on their next load (pages already in the server cache within about 5 minutes).' });
  };

  return (
    <Section title="Page transition"
      right={
        <div className="flex items-center gap-2 flex-wrap">
          {dirty && <span className="text-[11px] text-amber-700">unsaved changes</span>}
          <Btn small onClick={() => setRun(r => r + 1)} title="Preview the effect with these settings">▶ Play</Btn>
          <Btn small onClick={() => setT(DEFAULT)} disabled={busy}>Defaults</Btn>
          <Btn small kind="primary" onClick={save} disabled={busy || !dirty}>{busy ? 'Saving…' : 'Save'}</Btn>
        </div>
      }>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <p className="text-xs text-grayscale-1100 -mt-1">
        When a visitor moves from one technology or university page to another inside the site (similar technologies,
        a university&apos;s tech cards), the page being left dims under the loader for at least the minimum loader time,
        then the new page enters with the effect. Filters and the admin state preview do not trigger it. Visitors who
        ask their device for reduced motion get a plain fade.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="flex flex-col gap-3 text-sm">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={t.enabled} onChange={e => set('enabled', e.target.checked)} />
            <span className="font-medium">Transition on</span>
            <span className="text-[11px] text-grayscale-1100">off = the next page appears as soon as it arrives</span>
          </label>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="w-40 text-xs text-grayscale-1100">Effect</span>
            <Select value={t.effect} onChange={v => set('effect', v as Transition['effect'])}
              options={['fade', 'fade-up', 'slide', 'none']} labels={EFFECT_LABELS} />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="w-40 text-xs text-grayscale-1100">Effect time (ms)</span>
            <TextInput type="number" value={String(t.duration_ms)} onChange={v => set('duration_ms', ms(v))} />
            <span className="text-[11px] text-grayscale-1100">0–3000; 250–400 feels quick</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="w-40 text-xs text-grayscale-1100">Loader</span>
            <Select value={t.loader} onChange={v => set('loader', v as Transition['loader'])}
              options={['spinner', 'logo', 'bar', 'none']} labels={LOADER_LABELS} />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="w-40 text-xs text-grayscale-1100">Minimum loader time (ms)</span>
            <TextInput type="number" value={String(t.min_loader_ms)} onChange={v => set('min_loader_ms', ms(v))} />
            <span className="text-[11px] text-grayscale-1100">shown at least this long, even when the page is cached</span>
          </div>
        </div>
        <Preview t={t} run={run} />
      </div>
    </Section>
  );
};

/** Breadcrumbs above the title (Home › Universities › MIT › title) on tech and
 *  university pages; also switches the BreadcrumbList structured data and the
 *  crawler links, so what Google reads matches what visitors see. */
export const BreadcrumbsToggle: React.FC<{ data: any }> = ({ data }) => {
  const [on, setOn] = useState<boolean>(!!data?.breadcrumbs);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => { setOn(!!data?.breadcrumbs); }, [data]);
  const save = async (next: boolean) => {
    setBusy(true); setMsg(null); setOn(next);
    const { data: out, error } = await panelPost('tech_pages', 'settings_save', { breadcrumbs: next });
    setBusy(false);
    if (error) { setOn(!next); setMsg({ kind: 'error', text: error }); return; }
    setOn(!!(out as any)?.breadcrumbs);
    setMsg({ kind: 'ok', text: next ? 'Breadcrumbs on.' : 'Breadcrumbs off.' });
  };
  return (
    <Section title="Breadcrumbs">
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={on} disabled={busy} onChange={e => save(e.target.checked)} />
        <span className="font-medium">Show breadcrumbs above the title</span>
        {busy && <span className="text-[11px] text-grayscale-1100">saving…</span>}
      </label>
      <p className="text-xs text-grayscale-1100">
        Home › Universities › <i>university</i> › <i>technology</i>, on tech and university pages. The university uses its
        UTECH Website Label (Universities tab; until one is saved, the label from the Global Universities sheet, e.g.
        “Johns Hopkins”). Also adds the breadcrumb structured data Google can show in results; off removes both.
      </p>
    </Section>
  );
};

export default PageTransition;
